/**
 * Audio capture pipeline.
 *
 * Responsibilities:
 *   1. Obtain microphone via getUserMedia
 *   2. Set up AnalyserNode for visualisation (frequency/time data)
 *   3. Set up AudioWorklet for raw PCM capture
 *   4. Accumulate PCM into chunks (default 5 s)
 *   5. Resample to target sample rate via OfflineAudioContext
 *   6. Silence detection
 *   7. Invoke callback with ready chunks
 */

export interface AudioPipelineConfig {
  /** Chunk duration in seconds (default: 5). */
  chunkDuration?: number;
  /** Silence threshold — peak amplitude below this skips inference (default: 0.01). */
  silenceThreshold?: number;
  /** Target sample rate for the model (default: 16000). */
  targetSampleRate?: number;
  /** FFT size for the AnalyserNode (default: 2048). */
  fftSize?: number;
}

export type ChunkCallback = (pcm: Float32Array) => void;
export type SilenceCallback = () => void;
export type ProgressCallback = (progress: number) => void;

export class AudioPipeline {
  private ctx: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private stream: MediaStream | null = null;
  private workletNode: AudioWorkletNode | null = null;

  // Accumulator
  private buffer: Float32Array[] = [];
  private bufferedSamples = 0;

  // Config
  private chunkDuration: number;
  private silenceThreshold: number;
  private targetSampleRate: number;
  private fftSize: number;

  // Pre-allocated typed arrays for analyser reads
  freqData: Uint8Array<ArrayBuffer> | null = null;
  timeData: Uint8Array<ArrayBuffer> | null = null;

  /** Called when a chunk is ready (resampled PCM). */
  onChunk: ChunkCallback | null = null;
  /** Called when a chunk is silent and will be skipped. */
  onSilence: SilenceCallback | null = null;
  /** Called with chunk buffer progress (0..1) as samples accumulate. */
  onProgress: ProgressCallback | null = null;

  constructor(config?: AudioPipelineConfig) {
    this.chunkDuration = config?.chunkDuration ?? 5;
    this.silenceThreshold = config?.silenceThreshold ?? 0.01;
    this.targetSampleRate = config?.targetSampleRate ?? 16_000;
    this.fftSize = config?.fftSize ?? 2048;
  }

  /** Update target sample rate (e.g. after querying the model). */
  setTargetSampleRate(rate: number) {
    this.targetSampleRate = rate;
  }

  /** Native sample rate of the AudioContext (e.g. 48000). */
  get nativeSampleRate(): number {
    return this.ctx?.sampleRate ?? 48_000;
  }

  /** The AnalyserNode for driving visualisation from the main thread. */
  get analyserNode(): AnalyserNode | null {
    return this.analyser;
  }

  /** Device label of the active microphone. */
  get deviceName(): string {
    return this.stream?.getAudioTracks()[0]?.label?.slice(0, 40) ?? "—";
  }

  /**
   * Initialise the pipeline: request mic, set up audio graph.
   * Throws on permission denial or missing API.
   */
  async init(): Promise<void> {
    if (!navigator.mediaDevices?.getUserMedia) {
      throw new Error(
        "getUserMedia not available. Serve over HTTPS or http://localhost."
      );
    }

    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: { noiseSuppression: false },
    });

    this.ctx = new AudioContext();
    const source = this.ctx.createMediaStreamSource(this.stream);

    // AnalyserNode for visualisation
    this.analyser = this.ctx.createAnalyser();
    this.analyser.fftSize = this.fftSize;
    this.analyser.smoothingTimeConstant = 0.82;
    source.connect(this.analyser);
    this.freqData = new Uint8Array(this.analyser.frequencyBinCount);
    this.timeData = new Uint8Array(this.analyser.fftSize);

    // AudioWorklet for PCM capture
    // The worklet file is served as a static asset from public/
    await this.ctx.audioWorklet.addModule("/audio-processor.worklet.js");
    this.workletNode = new AudioWorkletNode(this.ctx, "pcm-capture");
    this.workletNode.port.onmessage = (ev: MessageEvent<Float32Array>) => {
      this.accumulateSamples(ev.data);
    };
    source.connect(this.workletNode);
  }

  /** Stop capturing and release resources. */
  dispose(): void {
    this.workletNode?.disconnect();
    this.stream?.getTracks().forEach((t) => t.stop());
    void this.ctx?.close();
    this.ctx = null;
    this.analyser = null;
    this.stream = null;
    this.workletNode = null;
    this.buffer = [];
    this.bufferedSamples = 0;
  }

  // ── Internal ────────────────────────────────────────────────────────────

  private accumulateSamples(samples: Float32Array): void {
    if (!this.ctx) return; // disposed — ignore queued worklet messages
    this.buffer.push(samples);
    this.bufferedSamples += samples.length;

    const chunkSamples = this.chunkDuration * this.nativeSampleRate;

    // Report chunk buffer progress (0..1)
    const progress = Math.min(this.bufferedSamples / chunkSamples, 1);
    this.onProgress?.(progress);

    if (this.bufferedSamples >= chunkSamples) {
      this.flushChunk(chunkSamples);
    }
  }

  private flushChunk(targetLength: number): void {
    // Concatenate accumulated buffers
    const raw = new Float32Array(targetLength);
    let offset = 0;
    for (const buf of this.buffer) {
      const take = Math.min(buf.length, targetLength - offset);
      raw.set(buf.subarray(0, take), offset);
      offset += take;
      if (offset >= targetLength) break;
    }

    // Reset accumulator — keep leftover samples
    let consumed = 0;
    const leftover: Float32Array[] = [];
    for (const buf of this.buffer) {
      consumed += buf.length;
      if (consumed > targetLength) {
        const excess = consumed - targetLength;
        leftover.push(buf.subarray(buf.length - excess));
      }
    }
    this.buffer = leftover;
    this.bufferedSamples = leftover.reduce((s, b) => s + b.length, 0);

    // Silence detection (peak amplitude)
    let peak = 0;
    for (let i = 0; i < raw.length; i++) {
      const abs = Math.abs(raw[i]!);
      if (abs > peak) peak = abs;
    }
    if (peak < this.silenceThreshold) {
      this.onSilence?.();
      return;
    }

    // Resample and deliver
    this.resampleAndDeliver(raw).catch((e) =>
      console.error("[audio-pipeline] resample failed:", e),
    );
  }

  private async resampleAndDeliver(raw: Float32Array): Promise<void> {
    const nativeSr = this.nativeSampleRate;
    const targetSr = this.targetSampleRate;

    let resampled: Float32Array;
    if (nativeSr === targetSr) {
      resampled = raw;
    } else {
      // Use OfflineAudioContext for native resampling
      const duration = raw.length / nativeSr;
      const offlineCtx = new OfflineAudioContext(
        1,
        Math.ceil(duration * targetSr),
        targetSr,
      );
      const buf = offlineCtx.createBuffer(1, raw.length, nativeSr);
      buf.getChannelData(0).set(raw);
      const src = offlineCtx.createBufferSource();
      src.buffer = buf;
      src.connect(offlineCtx.destination);
      src.start();
      const rendered = await offlineCtx.startRendering();
      resampled = rendered.getChannelData(0);
    }

    this.onChunk?.(resampled);
  }
}
